import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

// Marker types
export interface DriverMarkerData {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  isOnDelivery?: boolean;
  lastUpdated?: string;
}

export interface GasStationMarkerData {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

export interface CustomerMarkerData {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  address?: string;
}

export interface DeliveryMapProps {
  drivers?: DriverMarkerData[];
  gasStations?: GasStationMarkerData[];
  customers?: CustomerMarkerData[];
  className?: string;
}

// Giao Gas design-system marker colors
//   station = carbon-950, driver = propane-500, customer = info-500
// All pins are teardrop-shaped with a 2px white border to stay legible on any tile.
const PIN_STATION = '#0E0F11'; // carbon-950
const PIN_DRIVER = '#E8501C'; // propane-500
const PIN_CUSTOMER = '#1E6FBF'; // info-500

function teardropPin(color: string, size = 32) {
  // 32×40 viewBox — teardrop body + inner dot + white stroke
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 40" width="${size}" height="${(size * 40) / 32}">
      <path d="M16 1 C7.7 1 1 7.7 1 16 C1 26 16 39 16 39 C16 39 31 26 31 16 C31 7.7 24.3 1 16 1 Z"
        fill="${color}" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/>
      <circle cx="16" cy="16" r="4.5" fill="#FFFFFF"/>
    </svg>
  `;
  return 'data:image/svg+xml;base64,' + btoa(svg);
}

const gasStationIcon = new L.Icon({
  iconUrl: teardropPin(PIN_STATION, 32),
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40],
});

const driverIcon = new L.Icon({
  iconUrl: teardropPin(PIN_DRIVER, 32),
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40],
});

const customerIcon = new L.Icon({
  iconUrl: teardropPin(PIN_CUSTOMER, 28),
  iconSize: [28, 35],
  iconAnchor: [14, 35],
  popupAnchor: [0, -35],
});

// Format time ago for driver location timestamp
function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return 'just now';
  if (diffSecs < 120) return '1 min ago';
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)} mins ago`;
  if (diffSecs < 7200) return '1 hour ago';
  return `${Math.floor(diffSecs / 3600)} hours ago`;
}

// Component to fit map bounds to include all markers
function FitBounds({
  drivers,
  gasStations,
  customers,
}: {
  drivers: DriverMarkerData[];
  gasStations: GasStationMarkerData[];
  customers: CustomerMarkerData[];
}) {
  const map = useMap();

  useEffect(() => {
    const points: [number, number][] = [];

    // Add all marker positions
    gasStations.forEach((gs) => points.push([gs.latitude, gs.longitude]));
    drivers.forEach((d) => points.push([d.latitude, d.longitude]));
    customers.forEach((c) => points.push([c.latitude, c.longitude]));

    if (points.length === 0) {
      // Default to Ho Chi Minh City if no points
      map.setView([10.8231, 106.6297], 14);
    } else if (points.length === 1) {
      // Single point, use default zoom
      map.setView(points[0], 14);
    } else {
      // Fit bounds to include all points
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [map, drivers, gasStations, customers]);

  return null;
}

export function DeliveryMap({
  drivers = [],
  gasStations = [],
  customers = [],
  className,
}: DeliveryMapProps) {
  // Calculate initial center from first available marker
  const initialCenter: [number, number] =
    gasStations.length > 0
      ? [gasStations[0].latitude, gasStations[0].longitude]
      : drivers.length > 0
        ? [drivers[0].latitude, drivers[0].longitude]
        : customers.length > 0
          ? [customers[0].latitude, customers[0].longitude]
          : [10.8231, 106.6297]; // Default: Ho Chi Minh City

  return (
    <div className={className}>
      <MapContainer
        center={initialCenter}
        zoom={14}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />

        {/* Gas station markers */}
        {gasStations.map((gs) => (
          <Marker key={`gs-${gs.id}`} position={[gs.latitude, gs.longitude]} icon={gasStationIcon}>
            <Popup>
              <div className="text-sm font-semibold">{gs.name}</div>
            </Popup>
          </Marker>
        ))}

        {/* Driver markers */}
        {drivers.map((driver) => (
          <Marker
            key={`driver-${driver.id}`}
            position={[driver.latitude, driver.longitude]}
            icon={driverIcon}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold">{driver.name || 'Driver'}</div>
                {driver.lastUpdated && (
                  <div className="text-muted-foreground text-xs">
                    Updated {formatTimeAgo(driver.lastUpdated)}
                  </div>
                )}
                {driver.isOnDelivery && (
                  <div className="text-xs mt-1 text-propane-600">On delivery</div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Customer markers */}
        {customers.map((customer) => (
          <Marker
            key={`customer-${customer.id}`}
            position={[customer.latitude, customer.longitude]}
            icon={customerIcon}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold">{customer.name}</div>
                {customer.address && (
                  <div className="text-muted-foreground text-xs">{customer.address}</div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        <FitBounds drivers={drivers} gasStations={gasStations} customers={customers} />
      </MapContainer>
    </div>
  );
}
